import React, { memo, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import { Paper, Typography, Box, alpha, Chip } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const ParallelGatewayNode = memo(({ data, selected, id }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Green theme colors
  const nodeColor = data.color || theme.palette.success.main;
  const textColor = theme.palette.getContrastText(nodeColor);
  const borderColor = selected ? theme.palette.secondary.main : 'transparent';
  const hoverColor = alpha(nodeColor, 0.9);

  const handleDoubleClick = useCallback((event) => {
    event.stopPropagation();
    const editEvent = new CustomEvent('editNode', { detail: { nodeId: id } });
    window.dispatchEvent(editEvent);
  }, [id]);

  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const contextEvent = new CustomEvent('nodeContextMenu', {
      detail: {
        nodeId: id,
        x: event.clientX,
        y: event.clientY
      }
    });
    window.dispatchEvent(contextEvent);
  }, [id]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Paper
        elevation={selected ? 8 : 4}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
        sx={{
          padding: 2,
          minWidth: 100,
          minHeight: 100,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: nodeColor,
          color: textColor,
          border: `3px solid ${borderColor}`,
          cursor: 'move',
          position: 'relative',
          // Diamond shape using clip-path
          clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
          transition: theme.transitions.create([
            'transform', 'box-shadow', 'background-color'
          ], {
            duration: theme.transitions.duration.shorter,
          }),
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: theme.shadows[12],
            backgroundColor: hoverColor,
          },
          '&:focus-within': {
            outline: `2px solid ${theme.palette.secondary.main}`,
            outlineOffset: 4,
          },
          '&[aria-selected="true"]': {
            boxShadow: `0 0 0 4px ${alpha(theme.palette.secondary.main, 0.3)}`,
          },
        }}
        role="button"
        tabIndex={0}
        aria-label={`Parallel gateway node: ${data.label || 'Parallel Split'}`}
        aria-selected={selected}
      >
        {/* Top Input Handle */}
        <Handle
          type="target"
          position={Position.Top}
          style={{
            background: theme.palette.text.primary,
            border: `2px solid ${theme.palette.background.paper}`,
            width: 12,
            height: 12,
            top: -6,
          }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <Add
            sx={{
              fontSize: 32,
              mb: 0.5,
              color: textColor,
              filter: isDark ? 'none' : 'brightness(1.1)',
              fontWeight: 'bold',
            }}
          />
          <Typography
            variant="caption"
            fontWeight="600"
            sx={{
              color: textColor,
              fontSize: '0.8rem',
              lineHeight: 1.1,
              maxWidth: '70px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {data.label || 'Parallel'}
          </Typography>
        </Box>

        {/* Multiple Output Handles */}
        {/* Bottom Handle */}
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          style={{
            background: theme.palette.primary.main,
            border: `2px solid ${theme.palette.background.paper}`,
            width: 12,
            height: 12,
            bottom: -6,
          }}
        />

        {/* Left Handle */}
        <Handle
          type="source"
          position={Position.Left}
          id="left"
          style={{
            background: theme.palette.primary.main,
            border: `2px solid ${theme.palette.background.paper}`,
            width: 12,
            height: 12,
            left: -6,
          }}
        />

        {/* Right Handle */}
        <Handle
          type="source"
          position={Position.Right}
          id="right"
          style={{
            background: theme.palette.primary.main,
            border: `2px solid ${theme.palette.background.paper}`,
            width: 12,
            height: 12,
            right: -6,
          }}
        />
      </Paper>

      {/* Gateway Type Label */}
      <Chip
        label="PARALLEL"
        size="small"
        sx={{
          position: 'absolute',
          top: -25,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: theme.palette.success.main,
          color: theme.palette.success.contrastText,
          fontSize: '0.6rem',
          height: 18,
          minWidth: 55,
          opacity: selected ? 1 : 0.8,
          transition: theme.transitions.create('opacity'),
          fontWeight: 'bold',
        }}
      />

      {/* Connection Count Badge */}
      {data.connectionCount && (
        <Box
          sx={{
            position: 'absolute',
            top: -8,
            right: -8,
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.7rem',
            fontWeight: 'bold',
            border: `2px solid ${theme.palette.background.paper}`,
            boxShadow: theme.shadows[4],
          }}
        >
          {data.connectionCount}
        </Box>
      )}
    </Box>
  );
});

ParallelGatewayNode.displayName = 'ParallelGatewayNode';

export default ParallelGatewayNode;